import React, { useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTour } from '../contexts/TourContext';
import { HelpCircle, BookOpen, Shield, Cookie, FileText, MessageCircle } from 'lucide-react';
import helpIcon from '../images/HelpIcon.png';
import termsOfServiceIcon from '../images/TermsOfServiceIcon.png';
import cookiesIcon from '../images/CookiesIcon.png';
import privacyPolicyIcon from '../images/PrivacyPolicyIcon.png';
import { SupportRequestModal } from './SupportRequestModal';
import './HelpResourcesMenu.css';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  imageIcon?: string;
  action: () => void;
  badge?: boolean;
}

const routeToTourKeyMap: Record<string, string> = {
  '/dashboard': 'dashboard',
  '/applications': 'applications',
  '/resumes': 'resumes',
  '/profile': 'profile',
  '/settings': 'settings',
  '/job-search': 'jobSearch',
  '/agents': 'agents',
};

export const HelpResourcesMenu: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { startTour, isTourCompleted } = useTour();
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const currentTourKey = routeToTourKeyMap[location.pathname];
  const hasUncompletedTour = currentTourKey ? !isTourCompleted(currentTourKey) : false;

  const handleTourClick = useCallback(() => {
    if (currentTourKey) {
      startTour(currentTourKey);
      return;
    }
    navigate('/dashboard');
    setTimeout(() => startTour('dashboard'), 250);
  }, [currentTourKey, navigate, startTour]);

  const handleLegalLinkClick = (path: string) => {
    navigate(path);
  };

  const tourLabel = currentTourKey ? (hasUncompletedTour ? 'Take a Tour' : 'Retake Tour') : 'Take a Tour';

  const menuItems: MenuItem[] = [
    {
      id: 'tour',
      label: tourLabel,
      icon: <HelpCircle className="menu-item-icon" size={20} />,
      action: handleTourClick,
      badge: hasUncompletedTour,
    },
    {
      id: 'support',
      label: 'Contact Support',
      icon: <MessageCircle className="menu-item-icon" size={20} />,
      action: () => setIsSupportModalOpen(true),
    },
    {
      id: 'help',
      label: 'Help Center',
      icon: <BookOpen className="menu-item-icon" size={20} />,
      imageIcon: helpIcon,
      action: () => handleLegalLinkClick('/help'),
    },
    {
      id: 'terms',
      label: 'Terms of Service',
      icon: <FileText className="menu-item-icon" size={20} />,
      imageIcon: termsOfServiceIcon,
      action: () => handleLegalLinkClick('/terms-of-service'),
    },
    {
      id: 'privacy',
      label: 'Privacy Policy',
      icon: <Shield className="menu-item-icon" size={20} />,
      imageIcon: privacyPolicyIcon,
      action: () => handleLegalLinkClick('/privacy-policy'),
    },
    {
      id: 'cookies',
      label: 'Cookie Policy',
      icon: <Cookie className="menu-item-icon" size={20} />,
      imageIcon: cookiesIcon,
      action: () => handleLegalLinkClick('/cookie-policy'),
    },
  ];

  const handleToggle = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleCollapse = () => {
    setIsExpanded(false);
  };

  const handleMenuAction = (action: () => void) => {
    action();
    setIsExpanded(false);
  };

  return (
    <div className={`help-resources-menu ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <button
        className="help-resources-trigger"
        aria-label={isExpanded ? 'Collapse help menu' : 'Expand help menu'}
        title="Help & Resources"
        onClick={handleToggle}
      >
        <span className="help-trigger-icon">?</span>
        <span className="help-trigger-label">Help</span>
        {hasUncompletedTour && <span className="help-trigger-badge" />}
      </button>

      <div className="help-resources-panel" aria-hidden={!isExpanded}>
        <div className="help-resources-header">
          <h3 className="help-resources-title">Help & Resources</h3>
          <button
            type="button"
            className="help-resources-close"
            onClick={handleCollapse}
            aria-label="Collapse"
          >
            ×
          </button>
        </div>
        <div className="help-resources-items">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleMenuAction(item.action)}
              className="help-resources-item"
            >
              <div className="help-resources-item-icon">
                {item.imageIcon ? (
                  <img
                    src={item.imageIcon}
                    alt={item.label}
                    className="help-resources-item-image"
                  />
                ) : (
                  item.icon
                )}
                {item.badge && (
                  <span className="help-resources-item-badge" />
                )}
              </div>
              <span className="help-resources-item-label">{item.label}</span>
            </button>
          ))}
        </div>
      </div>
      
      {/* Support Request Modal */}
      <SupportRequestModal
        isOpen={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
      />
    </div>
  );
};
